import React, { useRef, useState } from 'react';
import { BASE_URL } from '../utilities/api';
import { format } from 'date-fns'; // Import date-fns helpers
import useModalA11y from '../hooks/useModalA11y';

const AVATAR_BASE_URL = BASE_URL + '/securelink/obj/avatars/small_';
const DEFAULT_AVATAR = '/images/default-avatar.png';

function DoctorCard({ doctor, onViewMoreTimes, onSelectTime}) {
    const [showBioModal, setShowBioModal] = useState(false);
    const bioDialogRef = useRef(null);

    const avatarSrc = doctor.avatarUrl || (doctor.avatar ? `${AVATAR_BASE_URL}${doctor.avatar}` : DEFAULT_AVATAR);

    const localTimeDisplay = format(doctor.nextAvail,'PP p') ;

    const handleOnSelectTimeClick = () => {
        onSelectTime(doctor, doctor.nextAvail);
    };

    const handleShowMoreClick = () => {
        setShowBioModal(true);
    };

    const handleCloseBioModal = () => {
        setShowBioModal(false);
    };

    const handleViewAvailabilityClick = () => {
        onViewMoreTimes();
    };

    useModalA11y({
        isOpen: showBioModal,
        dialogRef: bioDialogRef,
        onClose: handleCloseBioModal,
    });

    return (
        <>
            <div className="border rounded-lg shadow-md p-4 flex flex-col bg-white hover:shadow-lg transition-shadow duration-200 h-full">
                {/* Avatar and Name */}
                <div className="flex items-center mb-3">
                    <img
                        src={avatarSrc}
                        alt={`Avatar of ${doctor.name}`}
                        className="w-16 h-16 rounded-full mr-4 object-cover border flex-shrink-0"
                        onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_AVATAR; }}
                    />
                    <h3 className="text-xl font-semibold text-primary-dark">{doctor.name}</h3>
                </div>

                {/* Bio Truncated */}
                <p className="text-sm text-gray-600 overflow-hidden text-ellipsis line-clamp-3 min-h-[72px]">
                    {doctor.bio || 'No bio available.'}
                </p>

                {/* "Show More" link */}
                {doctor.bio && doctor.bio.length > 100 && (
                    <button
                        type="button"
                        className="text-primary-light text-xs mt-2 self-end hover:underline focus:outline-none"
                        onClick={handleShowMoreClick}
                    >
                        Show more...
                    </button>
                )}

                {/* Buttons */}
                <div className="space-y-2 mt-auto pt-4">
                    {localTimeDisplay && (
                        <button
                            type="button"
                            onClick={handleOnSelectTimeClick}
                            className="w-full px-4 py-2 bg-button text-white rounded-md hover:bg-button-light focus:outline-none focus:ring-2 focus:ring-button focus:ring-opacity-50 transition-colors duration-200 text-sm"
	                        >
	                            Choose next available:<br />
	                            {localTimeDisplay}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleViewAvailabilityClick}
                        className="w-full px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-opacity-50 transition-colors duration-200 text-sm"
                    >
	                        Choose another appointment time
                    </button>
                </div>
            </div>

            {/* --- Modal 1: Full Bio --- */}
            {showBioModal && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div
                        ref={bioDialogRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={`doctorBioTitle-${doctor.userid}`}
                        tabIndex={-1}
                        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative max-h-[90vh] overflow-y-auto"
                    >
                        <h4 id={`doctorBioTitle-${doctor.userid}`} className="text-lg font-semibold text-primary mb-4">{doctor.name}</h4>
                        <p className="text-sm text-neutral-dark mb-6">{doctor.bio || 'No bio available.'}</p>

                        <div className="space-y-4">
                            {localTimeDisplay && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        handleCloseBioModal();
                                        handleOnSelectTimeClick();
                                    }}
                                    className="w-full bg-button text-white py-2 rounded-md hover:bg-button-light focus:outline-none focus:ring-2 focus:ring-button focus:ring-opacity-50"
                                >
	                                    Choose next available ({localTimeDisplay})
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    handleCloseBioModal();
                                    handleViewAvailabilityClick();
                                }}
                                className="w-full bg-secondary text-white py-2 rounded-md hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-opacity-50"
                            >
	                                Choose another appointment time
                            </button>
                            <button
                                type="button"
                                onClick={handleCloseBioModal}
                                className="w-full bg-gray-300 text-gray-800 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default DoctorCard;
